import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "conditionsList", "template", "condition", "logicSection",
    "thresholdWrapper", "weeksLabel", "geoSpecific",
    "geoSearchInput", "geoSearchResults", "geoSelected",
    "geoScopeSelector", "geoScopeRadio"
  ]

  static STATUS_CHANGE_TYPES = ["market_type_change", "distress_change", "investor_change"]

  static TARGET_OPTIONS = {
    market_type_change: [
      ["any", "Any Change"],
      ["sellers", "Seller's Market"],
      ["balanced", "Balanced Market"],
      ["buyers", "Buyer's Market"]
    ],
    distress_change: [
      ["any", "Any Change"],
      ["low", "Low Distress"],
      ["moderate", "Moderate Distress"],
      ["high", "High Distress"]
    ],
    investor_change: [
      ["any", "Any Change"],
      ["low", "Low Activity"],
      ["moderate", "Moderate Activity"],
      ["high", "High Activity"]
    ]
  }

  connect() {
    this.conditionIndex = this.conditionTargets.length
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.searchTimeout = null
    this.highlightedGeoIndex = -1
    this._hideAllResults = this._hideAllGeoResults.bind(this)
    document.addEventListener("click", this._hideAllResults)
    this._updateLogicVisibility()
    this._initMetricSelects()
  }

  disconnect() {
    document.removeEventListener("click", this._hideAllResults)
  }

  addCondition() {
    const template = this.templateTarget.innerHTML
    const html = template.replace(/NEW_INDEX/g, new Date().getTime())
    this.conditionsListTarget.insertAdjacentHTML("beforeend", html)
    this.conditionIndex++
    this._updateLogicVisibility()

    // The template is rendered server-side with a blank AlertCondition (nil attributes),
    // so the cloned card is missing the $ prefix, correct placeholder, and has both
    // metric selects enabled. Initialize the UI based on the default condition type.
    const newCard = this.conditionsListTarget.lastElementChild

    // Fix position: the NEW_INDEX→timestamp replacement also sets the position
    // hidden field to a 13-digit value that overflows PostgreSQL integer columns.
    const positionField = newCard.querySelector("input[name*='[position]']")
    if (positionField) positionField.value = this.conditionIndex - 1

    this._initNewConditionCard(newCard)
  }

  removeCondition(event) {
    const card = event.target.closest("[data-alert-builder-target='condition']")
    if (!card) return

    const visibleConditions = this.conditionTargets.filter(c => c.style.display !== "none")
    if (visibleConditions.length <= 1) return

    const destroyField = card.querySelector(".destroy-field")
    if (destroyField && card.querySelector("input[name*='[id]']")) {
      destroyField.value = "1"
      card.style.display = "none"
    } else {
      card.remove()
    }
    this._updateLogicVisibility()
  }

  toggleConditionType(event) {
    const card = event.target.closest("[data-alert-builder-target='condition']")
    if (!card) return

    const condType = event.target.value
    const isStatusChange = this.constructor.STATUS_CHANGE_TYPES.includes(condType)
    const isPriceChange = condType === "price_change"
    const isInventoryChange = condType === "inventory_change"

    // Show/hide rows based on condition type
    const metricRow = card.querySelector(".condition-metric-row")
    const targetRow = card.querySelector(".condition-target-row")
    const thresholdTypeRow = card.querySelector(".condition-threshold-type-row")
    const directionRow = card.querySelector(".condition-direction-row")
    const weeksLabel = card.querySelector("[data-alert-builder-target='weeksLabel']")
    const thresholdTypeSelect = card.querySelector(".condition-threshold-type-select")

    // Metric row: only for price_change
    // Disable hidden selects so they don't override the visible one on submit
    // (both bind to the :metric field — the browser sends the last enabled one)
    const metricSelect = metricRow?.querySelector("select")
    const targetSelect = targetRow?.querySelector("select")
    if (metricRow) {
      metricRow.style.display = isPriceChange ? "" : "none"
      if (metricSelect) metricSelect.disabled = !isPriceChange
    }

    // Target row: only for status change types
    if (targetRow) {
      targetRow.style.display = isStatusChange ? "" : "none"
      if (targetSelect) targetSelect.disabled = !isStatusChange
      if (isStatusChange) this._updateTargetOptions(card, condType)
    }

    // Threshold type + direction: only for price/inventory change
    if (thresholdTypeRow) thresholdTypeRow.style.display = isStatusChange ? "none" : ""
    if (directionRow) directionRow.style.display = isStatusChange ? "none" : ""

    // Weeks label
    if (weeksLabel) {
      weeksLabel.textContent = isInventoryChange ? "reporting periods" : "weeks"
    }

    // Swap threshold type options for price vs inventory
    if (thresholdTypeSelect && !isStatusChange) {
      if (isPriceChange) {
        thresholdTypeSelect.innerHTML = '<option value="dollar">Dollar Amount ($)</option><option value="percent">Percent Change (%)</option>'
      } else if (isInventoryChange) {
        thresholdTypeSelect.innerHTML = '<option value="total">Total Value</option><option value="percent">Percent Change (%)</option>'
      }
      this._updateThresholdUI(card, thresholdTypeSelect.value, isPriceChange)
    }
  }

  toggleThresholdType(event) {
    const card = event.target.closest("[data-alert-builder-target='condition']")
    if (!card) return

    const conditionTypeSelect = card.querySelector(".condition-type-select")
    const isPriceChange = conditionTypeSelect && conditionTypeSelect.value === "price_change"
    this._updateThresholdUI(card, event.target.value, isPriceChange)
  }

  _updateTargetOptions(card, condType) {
    const targetSelect = card.querySelector(".condition-target-select")
    if (!targetSelect) return

    const options = this.constructor.TARGET_OPTIONS[condType]
    if (!options) return

    targetSelect.innerHTML = options.map(([val, label]) =>
      `<option value="${val}">${label}</option>`
    ).join("")
  }

  _updateThresholdUI(card, thresholdType, isPriceChange) {
    const thresholdWrapper = card.querySelector("[data-alert-builder-target='thresholdWrapper']")
    const thresholdInput = card.querySelector(".condition-threshold-input")
    if (!thresholdWrapper) return

    // Remove existing prefix/suffix
    const prefix = thresholdWrapper.querySelector(".condition-threshold-prefix")
    const suffix = thresholdWrapper.querySelector(".condition-threshold-suffix-label")
    if (prefix) prefix.remove()
    if (suffix) suffix.remove()

    if (thresholdType === "percent") {
      thresholdWrapper.insertAdjacentHTML("beforeend", '<span class="condition-threshold-suffix-label">%</span>')
      if (thresholdInput) {
        thresholdInput.step = "1"
        thresholdInput.placeholder = "10"
        // Reset absurd dollar values when switching to percent
        if (!thresholdInput.value || parseFloat(thresholdInput.value) >= 100) thresholdInput.value = 10
      }
    } else if (isPriceChange) {
      thresholdWrapper.insertAdjacentHTML("afterbegin", '<span class="condition-threshold-prefix">$</span>')
      if (thresholdInput) {
        thresholdInput.step = "1"
        thresholdInput.placeholder = "25,000"
        // Reset small percent values or empty when switching to dollar
        if (!thresholdInput.value || parseFloat(thresholdInput.value) < 100) thresholdInput.value = 10000
      }
    } else {
      // inventory total
      if (thresholdInput) {
        thresholdInput.step = "1"
        thresholdInput.placeholder = "50"
        if (!thresholdInput.value) thresholdInput.value = 50
      }
    }
  }

  _initNewConditionCard(card) {
    if (!card) return
    const condTypeSelect = card.querySelector(".condition-type-select")
    if (!condTypeSelect) return

    const condType = condTypeSelect.value
    const isStatusChange = this.constructor.STATUS_CHANGE_TYPES.includes(condType)
    const isPriceChange = condType === "price_change"
    const isInventoryChange = condType === "inventory_change"

    // Show/hide rows
    const metricRow = card.querySelector(".condition-metric-row")
    const targetRow = card.querySelector(".condition-target-row")
    const thresholdTypeRow = card.querySelector(".condition-threshold-type-row")
    const directionRow = card.querySelector(".condition-direction-row")
    const weeksLabel = card.querySelector("[data-alert-builder-target='weeksLabel']")
    const thresholdTypeSelect = card.querySelector(".condition-threshold-type-select")

    const metricSelect = metricRow?.querySelector("select")
    const targetSelect = targetRow?.querySelector("select")

    if (metricRow) {
      metricRow.style.display = isPriceChange ? "" : "none"
      if (metricSelect) metricSelect.disabled = !isPriceChange
    }
    if (targetRow) {
      targetRow.style.display = isStatusChange ? "" : "none"
      if (targetSelect) targetSelect.disabled = !isStatusChange
      if (isStatusChange) this._updateTargetOptions(card, condType)
    }
    if (thresholdTypeRow) thresholdTypeRow.style.display = isStatusChange ? "none" : ""
    if (directionRow) directionRow.style.display = isStatusChange ? "none" : ""
    if (weeksLabel) weeksLabel.textContent = isInventoryChange ? "reporting periods" : "weeks"

    // Default metric to Median (p50_price) — template renders AlertCondition.new with nil metric,
    // so the browser defaults to the first option (p25_price) instead of matching the controller default.
    if (metricSelect && isPriceChange) metricSelect.value = "p50_price"

    // Set default lookback weeks (template renders without a value since AlertCondition.new has nil)
    const lookbackInput = card.querySelector(".condition-weeks-input")
    if (lookbackInput && !lookbackInput.value) lookbackInput.value = 4

    // Set up threshold prefix/suffix/placeholder and default value
    if (thresholdTypeSelect && !isStatusChange) {
      this._updateThresholdUI(card, thresholdTypeSelect.value, isPriceChange)
      const thresholdInput = card.querySelector(".condition-threshold-input")
      if (thresholdInput && !thresholdInput.value) {
        thresholdInput.value = isPriceChange ? 10000 : 50
      }
    }
  }

  _initMetricSelects() {
    // On page load, disable whichever metric/target select is hidden for each
    // condition so only the relevant one submits with the form.
    this.conditionTargets.forEach(card => {
      const condTypeSelect = card.querySelector(".condition-type-select")
      if (!condTypeSelect) return
      const condType = condTypeSelect.value
      const isPriceChange = condType === "price_change"
      const isStatusChange = this.constructor.STATUS_CHANGE_TYPES.includes(condType)
      const metricSelect = card.querySelector(".condition-metric-row select")
      const targetSelect = card.querySelector(".condition-target-select")
      if (metricSelect) metricSelect.disabled = !isPriceChange
      if (targetSelect) targetSelect.disabled = !isStatusChange
    })
  }

  _updateLogicVisibility() {
    if (!this.hasLogicSectionTarget) return
    const visibleConditions = this.conditionTargets.filter(c => c.style.display !== "none")
    this.logicSectionTarget.style.display = visibleConditions.length >= 2 ? "" : "none"
  }

  toggleGeoScope(event) {
    const card = event.target.closest("[data-alert-builder-target='condition']")
    if (!card) return

    const value = event.target.value
    const geoSpecific = card.querySelector("[data-alert-builder-target='geoSpecific']")
    const geoTypeHidden = card.querySelector(".condition-geo-type-hidden")
    const geoIdHidden = card.querySelector(".condition-geo-id-hidden")

    const geoScopeHidden = card.querySelector(".condition-geo-scope-hidden")

    if (value === "specific") {
      if (geoSpecific) geoSpecific.style.display = ""
      if (geoTypeHidden) geoTypeHidden.value = ""
    } else {
      if (geoSpecific) geoSpecific.style.display = "none"
      if (geoTypeHidden) geoTypeHidden.value = value
      if (geoIdHidden) geoIdHidden.value = ""
      if (geoScopeHidden) geoScopeHidden.value = "self"
      this._clearGeoInCard(card)
    }
  }

  // --- Geography Search ---

  onGeoSearchInput(event) {
    clearTimeout(this.searchTimeout)
    this.highlightedGeoIndex = -1
    const query = event.target.value.trim()
    const card = event.target.closest("[data-alert-builder-target='condition']")
    const results = card?.querySelector("[data-alert-builder-target='geoSearchResults']")

    if (query.length < 2) {
      if (results) results.style.display = "none"
      return
    }

    this.searchTimeout = setTimeout(() => this._geoSearch(query, card), 300)
  }

  onGeoSearchKeydown(event) {
    const card = event.target.closest("[data-alert-builder-target='condition']")
    const resultsEl = card?.querySelector("[data-alert-builder-target='geoSearchResults']")
    if (!resultsEl || resultsEl.style.display === "none") return

    const items = resultsEl.querySelectorAll(".condition-geo-result-item[data-geo-id]")
    if (!items.length) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlightedGeoIndex = Math.min(this.highlightedGeoIndex + 1, items.length - 1)
      this._updateGeoHighlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlightedGeoIndex = Math.max(this.highlightedGeoIndex - 1, 0)
      this._updateGeoHighlight(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this.highlightedGeoIndex >= 0 && items[this.highlightedGeoIndex]) {
        const item = items[this.highlightedGeoIndex]
        this._selectGeo(card, item.dataset.geoId, item.dataset.geoName, item.dataset.geoType)
      }
    } else if (event.key === "Escape") {
      resultsEl.style.display = "none"
      this.highlightedGeoIndex = -1
    }
  }

  selectGeoResult(event) {
    const item = event.target.closest(".condition-geo-result-item")
    if (!item) return
    const card = item.closest("[data-alert-builder-target='condition']")
    this._selectGeo(card, item.dataset.geoId, item.dataset.geoName, item.dataset.geoType)
  }

  clearGeo(event) {
    const card = event.target.closest("[data-alert-builder-target='condition']")
    if (!card) return
    this._clearGeoInCard(card)
  }

  syncGeoScope(event) {
    const card = event.target.closest("[data-alert-builder-target='condition']")
    if (!card) return
    const geoScopeHidden = card.querySelector(".condition-geo-scope-hidden")
    if (geoScopeHidden) geoScopeHidden.value = event.target.value
  }

  // --- Private helpers ---

  async _geoSearch(query, card) {
    if (!card) return
    const resultsEl = card.querySelector("[data-alert-builder-target='geoSearchResults']")
    if (!resultsEl) return

    try {
      const response = await fetch(`/api/geographies/search?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json", ...(this.csrfToken ? { "X-CSRF-Token": this.csrfToken } : {}) },
        credentials: "same-origin"
      })
      if (!response.ok) return
      const results = await response.json()
      this.highlightedGeoIndex = -1
      this._renderGeoResults(resultsEl, results)
    } catch (error) {
      console.error("Geo search error:", error)
    }
  }

  _renderGeoResults(resultsEl, results) {
    if (results.length === 0) {
      resultsEl.innerHTML = '<div class="condition-geo-result-item condition-geo-no-results">No results found</div>'
      resultsEl.style.display = "block"
      return
    }

    const groups = {}
    const typeOrder = []
    results.forEach(r => {
      if (!groups[r.geo_type]) {
        groups[r.geo_type] = []
        typeOrder.push(r.geo_type)
      }
      groups[r.geo_type].push(r)
    })

    let html = ""
    typeOrder.forEach(type => {
      const label = groups[type][0].geo_type_label || type.toUpperCase()
      html += `<div class="condition-geo-result-header">${label}</div>`
      groups[type].forEach(r => {
        html += `<div class="condition-geo-result-item" data-geo-id="${r.id}" data-geo-name="${this._escapeHtml(r.display_name)}" data-geo-type="${r.geo_type}" data-action="click->alert-builder#selectGeoResult">${r.display_name}</div>`
      })
    })

    resultsEl.innerHTML = html
    resultsEl.style.display = "block"
  }

  _selectGeo(card, id, name, geoType) {
    if (!card) return
    const geoIdHidden = card.querySelector(".condition-geo-id-hidden")
    const searchInput = card.querySelector("[data-alert-builder-target='geoSearchInput']")
    const resultsEl = card.querySelector("[data-alert-builder-target='geoSearchResults']")
    const selectedEl = card.querySelector("[data-alert-builder-target='geoSelected']")

    if (geoIdHidden) geoIdHidden.value = id
    if (resultsEl) resultsEl.style.display = "none"
    if (searchInput) {
      searchInput.value = ""
      searchInput.style.display = "none"
    }
    if (selectedEl) {
      selectedEl.innerHTML = `<span class="condition-geo-chip"><span class="condition-geo-chip-name">${this._escapeHtml(name)}</span><button type="button" class="condition-geo-chip-remove" data-action="click->alert-builder#clearGeo" title="Remove">&times;</button></span>`
      selectedEl.style.display = ""
      selectedEl.dataset.geoType = geoType || ""
    }

    this._updateGeoScopeSelector(card, geoType)
  }

  _clearGeoInCard(card) {
    const geoIdHidden = card.querySelector(".condition-geo-id-hidden")
    const searchInput = card.querySelector("[data-alert-builder-target='geoSearchInput']")
    const selectedEl = card.querySelector("[data-alert-builder-target='geoSelected']")

    if (geoIdHidden) geoIdHidden.value = ""
    if (searchInput) {
      searchInput.value = ""
      searchInput.style.display = ""
      searchInput.focus()
    }
    if (selectedEl) {
      selectedEl.innerHTML = ""
      selectedEl.style.display = "none"
      delete selectedEl.dataset.geoType
    }

    this._updateGeoScopeSelector(card, null)
  }

  _updateGeoScopeSelector(card, geoType) {
    const scopeSelector = card.querySelector("[data-alert-builder-target='geoScopeSelector']")
    if (!scopeSelector) return

    const geoScopeHidden = card.querySelector(".condition-geo-scope-hidden")

    if (!geoType || geoType === "zip") {
      // Zip codes have no children — hide scope selector, reset to "self"
      scopeSelector.style.display = "none"
      const selfRadio = scopeSelector.querySelector("input[value='self']")
      if (selfRadio) selfRadio.checked = true
      if (geoScopeHidden) geoScopeHidden.value = "self"
      return
    }

    // Show the scope selector
    scopeSelector.style.display = ""

    // Update the "self" label text
    const selfLabel = scopeSelector.querySelector("[data-scope-self]")
    if (selfLabel) {
      const typeLabel = geoType === "msa" ? "MSA" : "City"
      selfLabel.textContent = `This ${typeLabel} only`
    }

    // Build scope options dynamically
    const optionsContainer = scopeSelector.querySelector(".condition-geo-scope-options")
    if (!optionsContainer) return

    // Get the field name prefix from the existing radio button
    const existingRadio = optionsContainer.querySelector("input[type='radio']")
    const radioName = existingRadio ? existingRadio.name : ""

    if (geoScopeHidden) geoScopeHidden.value = "self"

    let optionsHtml = `
      <label class="condition-geo-scope-option">
        <input type="radio" name="${radioName}" value="self" checked data-alert-builder-target="geoScopeRadio" data-action="change->alert-builder#syncGeoScope">
        <span class="condition-geo-scope-option-card">
          <span class="condition-geo-scope-option-text" data-scope-self>This ${geoType === "msa" ? "MSA" : "City"} only</span>
        </span>
      </label>`

    if (geoType === "msa") {
      optionsHtml += `
      <label class="condition-geo-scope-option" data-scope-for="msa">
        <input type="radio" name="${radioName}" value="cities_and_zips" data-alert-builder-target="geoScopeRadio" data-action="change->alert-builder#syncGeoScope">
        <span class="condition-geo-scope-option-card">
          <span class="condition-geo-scope-option-text">All Cities and All Zip Codes in this MSA</span>
        </span>
      </label>
      <label class="condition-geo-scope-option" data-scope-for="msa">
        <input type="radio" name="${radioName}" value="cities" data-alert-builder-target="geoScopeRadio" data-action="change->alert-builder#syncGeoScope">
        <span class="condition-geo-scope-option-card">
          <span class="condition-geo-scope-option-text">All Cities in this MSA</span>
        </span>
      </label>`
    }

    if (geoType === "msa" || geoType === "city") {
      optionsHtml += `
      <label class="condition-geo-scope-option" data-scope-for="msa city">
        <input type="radio" name="${radioName}" value="zips" data-alert-builder-target="geoScopeRadio" data-action="change->alert-builder#syncGeoScope">
        <span class="condition-geo-scope-option-card">
          <span class="condition-geo-scope-option-text">All Zip Codes in this ${geoType === "msa" ? "MSA" : "City"}</span>
        </span>
      </label>`
    }

    optionsContainer.innerHTML = optionsHtml
  }

  _updateGeoHighlight(items) {
    items.forEach((item, i) => {
      item.classList.toggle("highlighted", i === this.highlightedGeoIndex)
    })
    if (items[this.highlightedGeoIndex]) {
      items[this.highlightedGeoIndex].scrollIntoView({ block: "nearest" })
    }
  }

  _hideAllGeoResults(event) {
    if (this.element.contains(event.target)) return
    this.geoSearchResultsTargets.forEach(el => { el.style.display = "none" })
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  updateLogicDisplay() {
    // Radio buttons handle the logic value via Rails form binding
  }
}
